import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { useRef } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { Button } from 'foundations-components/transitional/components/Button';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import LineItemDiscount from './LineItemDiscount';
import LineItemDescription from './LineItemDescription';
import LineItemPrice from './LineItemPrice';
import LineItemTax from './LineItemTax';
import { DEFAULT_QUANTITIES } from '../../constants/BuyerSelectedQuantitySettings';
import { getBuyerOverridesQuantity } from '../../state/BuyerOverrides/selectors';
import LineItemPriceOpenAmount from './LineItemPriceOpenAmount';
import LineItemQuantityAndUnitPrice from './LineItemUnitPrice';
import { getLineItemRecurringFrequency } from '../../utils/recurring';
import FirstPaymentDateSubtext from './FirstPaymentDateSubtext';
import { isFutureStartDateCheckoutLineItem, isLineItemWithTax, isRelativeStartDateCheckoutLineItem } from '../../types/utils/typeguards';
import { useSubtotals } from '../../context/SubtotalsProvider';
import useBuyerOverridesLoading from '../../hooks/useBuyerOverridesLoading';
import LineItemCheckoutFee from './LineItemCheckoutFee';
import { AddIcon } from 'foundations-assets/icons/components/AddIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
// This is custom built because UITruncateMultiline only supports character limit truncation
// and cuts off content on screenreaders
const LineTruncatedDiv = styled.div.withConfig({
  displayName: "LineItem__LineTruncatedDiv"
})(["-webkit-line-clamp:2;-webkit-box-orient:vertical;display:-webkit-inline-box;overflow:hidden;"]);
const ButtonOptionalAdd = ({
  onClick
}) => /*#__PURE__*/_jsxs(Button, {
  "data-test-id": "add-optional-line-item",
  use: "transparent",
  margin: "0",
  padding: "0",
  marginBlockEnd: "50",
  onClick: onClick,
  children: [/*#__PURE__*/_jsx(AddIcon, {
    color: "icon-interactive-default"
  }), /*#__PURE__*/_jsx(FormattedMessage, {
    message: "payments.lineItemOptionalAddButton"
  })]
});
export const ButtonOptionalRemove = ({
  onClick
}) => /*#__PURE__*/_jsx(Button, {
  use: "transparent",
  margin: "0",
  padding: "0",
  marginBlockStart: "300",
  onClick: onClick,
  children: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "payments.lineItemOptionalRemoveButton"
  })
});
const LineItem = _ref => {
  var _lineItem$tax;
  let {
    currencyCode,
    error,
    lineItem,
    readOnly,
    toggleSelected,
    selected,
    sessionOpen,
    checkoutFeeAdjustment,
    taxDisplayConfig
  } = _ref;
  const ref = useRef(null);
  const buyerOverridesQuantity = useSelector(state => getBuyerOverridesQuantity(state, lineItem.id));
  const hasBuyerOverridesQuantity = buyerOverridesQuantity && buyerOverridesQuantity !== lineItem.quantity;
  const isBuyerOverridesLoading = useBuyerOverridesLoading();
  const {
    quantityConfiguration = null,
    hasEditableQuantity = false,
    hasEditablePrice = false,
    isOptional: _isOptional = false
  } = lineItem.customConfiguration || {};
  const {
    min: quantityMin,
    max: quantityMax
  } = quantityConfiguration || DEFAULT_QUANTITIES;
  const isEditableQuantity = !readOnly && hasEditableQuantity;
  const isOpenAmount = !readOnly && hasEditablePrice;
  const isOptional = !readOnly && _isOptional;
  const isFutureStartDateLineItem = isFutureStartDateCheckoutLineItem(lineItem) || isRelativeStartDateCheckoutLineItem(lineItem);
  const hasFutureItemExpiredError = error === null || error === void 0 ? void 0 : error.hasFutureItemExpired;
  const hasFutureValidationError = error === null || error === void 0 ? void 0 : error.hasFutureValidationError;
  const hasPresentValidationError = error === null || error === void 0 ? void 0 : error.hasValidationError;
  const hasInputValidationError = isFutureStartDateLineItem ? hasFutureValidationError : hasPresentValidationError;
  const {
    recurringSubtotals
  } = useSubtotals();
  const hasMultipleSubscriptions = recurringSubtotals.length > 1;

  /**
   * sub-elements
   */
  const name = /*#__PURE__*/_jsx(Text, {
    as: "h6",
    "data-test-id": "line-item-name",
    marginBlock: "100",
    children: /*#__PURE__*/_jsx(LineTruncatedDiv, {
      ref: ref,
      children: lineItem.name
    })
  });
  // Comparing scrollHeight and clientHeight tells us whether the content _would_ have overflown if not truncated
  // so we can add a tooltip for truncated content. In this case, whether there would be >2 text lines in the div
  const nameWithTooltip = ref.current && ref.current.scrollHeight > ref.current.clientHeight ? /*#__PURE__*/_jsx(Tooltip, {
    title: lineItem.name,
    children: name
  }) : name;
  const maybeDescription = lineItem.description && /*#__PURE__*/_jsx(LineItemDescription, {
    description: lineItem.description
  });
  const maybeOptionalAdd = selected === false && sessionOpen && /*#__PURE__*/_jsx(ButtonOptionalAdd, {
    onClick: toggleSelected
  });
  const maybeOptionalRemove = selected === true && sessionOpen && /*#__PURE__*/_jsx(ButtonOptionalRemove, {
    onClick: toggleSelected
  });
  const maybeQuantityAndUnitPrice = (isEditableQuantity && selected !== false || lineItem.quantity > 1) && /*#__PURE__*/_jsx(LineItemQuantityAndUnitPrice, {
    id: lineItem.id,
    currencyCode: currencyCode,
    effectiveUnitPrice: lineItem.effectiveUnitPrice,
    pricing: lineItem.pricing,
    quantity: lineItem.quantity,
    quantityMin: quantityMin,
    quantityMax: quantityMax,
    isEditable: !!isEditableQuantity && (!isOptional || selected === true),
    error: hasInputValidationError
  });
  const maybeItemTotal = selected === false && isOpenAmount ? null : hasBuyerOverridesQuantity ? /*#__PURE__*/_jsx(LineItemPrice, {
    currencyCode: currencyCode,
    lineItem: lineItem,
    isBuyerOverridesLoading: isBuyerOverridesLoading
  }) : isOpenAmount ? /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(LineItemPriceOpenAmount, {
      currencyCode: currencyCode,
      error: hasInputValidationError,
      lineItem: lineItem
    }), lineItem.recurringBillingTerms && /*#__PURE__*/_jsx(Text, {
      as: "small",
      color: "text-core-subtle",
      marginBlockStart: "100",
      "data-test-id": "open-amount-recurring",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `payments.openAmountRecurring.${getLineItemRecurringFrequency(lineItem)}`
      })
    })]
  }) : /*#__PURE__*/_jsx(LineItemPrice, {
    currencyCode: currencyCode,
    lineItem: lineItem
  });
  const maybeDiscount = lineItem.discount && lineItem.discountAmount && /*#__PURE__*/_jsx(LineItemDiscount, {
    currencyCode: currencyCode,
    discountAmount: lineItem.discountAmount,
    unadjustedAmount: lineItem.unadjustedAmount,
    value: lineItem.discount.value,
    valueType: lineItem.discount.valueType
  });
  const maybeTax = taxDisplayConfig ? taxDisplayConfig.shouldDisplayLineItemTaxes && /*#__PURE__*/_jsx(LineItemTax, {
    taxDisplayConfig: taxDisplayConfig,
    currencyCode: currencyCode,
    label: lineItem.tax && lineItem.tax.label || taxDisplayConfig.lineItemTaxLabel,
    taxAmount: lineItem.taxAmount
  }) : isLineItemWithTax(lineItem) && /*#__PURE__*/_jsx(LineItemTax, {
    taxDisplayConfig: taxDisplayConfig,
    currencyCode: currencyCode,
    label: ((_lineItem$tax = lineItem.tax) === null || _lineItem$tax === void 0 ? void 0 : _lineItem$tax.label) || null,
    taxAmount: lineItem.taxAmount
  });
  const maybeCheckoutFee = checkoutFeeAdjustment && /*#__PURE__*/_jsx(LineItemCheckoutFee, {
    checkoutFeeAdjustment: checkoutFeeAdjustment
  });

  /**
   * Future start date
   */
  const maybePaymentStartDate = (isFutureStartDateCheckoutLineItem(lineItem) || isRelativeStartDateCheckoutLineItem(lineItem)) && /*#__PURE__*/_jsx(FirstPaymentDateSubtext, {
    lineItem: lineItem
  });
  const numPayments = lineItem.recurringBillingTerms && lineItem.recurringBillingTerms.numPayments;
  const showFixedNumPayments = (isFutureStartDateLineItem || hasMultipleSubscriptions) && Boolean(numPayments);

  // Show the number of fixed future payments for FSD and multiple subscriptions and hide RecurringTotalsMessage from Total.tsx
  const maybeFixedNumPayments = showFixedNumPayments ? /*#__PURE__*/_jsx(Text, {
    as: "small",
    color: "text-core-subtle",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "payments.lineItemRecurringCharges.numPayments",
      options: {
        count: numPayments
      }
    })
  }) : null;
  const maybeFutureItemItemExpiredError = hasFutureItemExpiredError && /*#__PURE__*/_jsx(Text, {
    as: "small",
    color: "text-alert-default",
    marginBlockStart: "200",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "payments.futureCharges.expiredPayment"
    })
  });
  const isSelectable = selected === false && sessionOpen;
  return /*#__PURE__*/_jsxs(View, {
    backgroundColor: "fill-surface-default",
    padding: isSelectable ? '400' : undefined,
    borderStyle: isSelectable ? 'solid' : undefined,
    borderColor: isSelectable ? 'border-core-default' : undefined,
    borderWidth: isSelectable ? '100' : undefined,
    borderRadius: isSelectable ? '100' : undefined,
    children: [/*#__PURE__*/_jsx(Flex, {
      flexDirection: "row",
      children: maybePaymentStartDate
    }), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "row",
      justifyContent: "space-between",
      children: [/*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        alignItems: "start",
        flex: "0 0 55%",
        children: [nameWithTooltip, maybeQuantityAndUnitPrice, maybeDescription, maybeFutureItemItemExpiredError, maybeOptionalRemove]
      }), /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        alignItems: "end",
        flex: "0 0 35%",
        children: [maybeOptionalAdd, maybeItemTotal, maybeDiscount, maybeTax, maybeCheckoutFee, maybeFixedNumPayments]
      })]
    })]
  });
};
export default LineItem;